import { property, supertypeClass } from '@haventech/amorphic';
import { BaseWaiverFlatExtras, Policy } from '../../../common/js/customer/Policy';
import { CoverpathBasePolicy } from './CoverpathBasePolicy';
import { CoverpathCustomer } from './CoverpathCustomer';
import { CasApplication } from './CasApplication';
import { LoanType } from '../../js/interfaces/CoverpathTypes';
import { CoverpathUtils } from '../../js/static/CoverpathUtils';
import { UwPolicyRate, UwPolicyRateResult } from '../../../common/js/customer/UwPolicyRate';
import { FollowupSummary } from '../FollowupSummary';
import { PolicyFollower } from '../../../common/js/customer/PolicyFollower';
import { Agent } from '../../../common/js/customer/Agent';

import { PremiumRequest, ProductServiceClient } from '../../../common/js/productService/ProductServiceClient';
import {
  DividendOption,
  LowFaceWholeLife,
  Product,
  RiderType,
  TeleCMIConstants,
} from '../../../common/js/static/Product';
import { RateClasses } from '../../../common/js/static/RateClasses';
import { PremiumBreakdown } from '../../../common/js/customer/PremiumBreakdown';
import { Utils } from '../../../common/js/Utils';
import { CommonUtils } from '../../../common/js/static/CommonUtils';
import { BaseQuote } from '../../../common/js/quote/BaseQuote';
import { CoverpathWholeLifeQuote } from '../quote/coverpath/CoverpathWholeLifeQuote';
import { AgentProxy } from '../../../common/js/agent/AgentProxy';
import { OpsProxy } from '../../../common/js/agent/OpsProxy';
import { Entitlement } from '../../../common/js/agent/UserPrincipal';
import { PolicyAgent } from '../../../common/js/customer/PolicyAgent';
import { PolicyAmendmentProperty } from '../../../common/js/static/PolicyAmendmentProperty';
import { PolicyRider } from '../../../common/js/customer/PolicyRider';
import { FlatExtraAmendments } from '../../../common/js/static/FlatExtraAmendments';
import { ApplicationSourceType } from '../../../common/js/customer/Application';
import {
  POLICY_CHANGE_EDITOR,
  POLICY_CHANGE_HISTORY_STATUS,
} from '../../../common/js/static/PolicyChangeHistoryStatus';
import { PolicyChangeHistory } from '../../../common/js/customer/PolicyChangeHistory';
import { Exchange1035 } from '../../../common/js/quote/coverpath/Exchange1035';
import { constants as BasicIllustrationConstants } from '../wholelife/basicIllustration/templates/constantsForPdf';
import { ChronicIllnessRiderSupplement } from '../../../common/js/customer/ChronicIllnessRiderSupplement';
import { AgencyCorpsRelationship, CDMProducerInfo, CdmService } from '../../../common/js/services/CdmService';
import { ProductType } from '../../../common/js/interfaces/CommonTypes';
import { UICategory, UILoggerSingleton } from '../../../common/js/logger/UILoggerSingleton';

@supertypeClass({ toClient: true, toServer: true })
export class CoverpathPolicy extends CoverpathBasePolicy {
  @property({ getType: () => CoverpathCustomer })
  customer: CoverpathCustomer;

  @property()
  product: string = 'CASTerm';

  @property({
    getType: () => CasApplication,
    comment: 'ref to Application table, which holds application number and policies under that application',
  })
  application: CasApplication;

  @property({
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'determines if the fund for purchasing the policy is withdrawn from retirement account; true means Yes',
  })
  isDOL: boolean;

  @property({
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'is it an automatic premium loan; true means Yes',
  })
  wholeAPL: boolean;

  @property({ comment: 'loan interest type for a whole life policy' })
  loanInterestType: LoanType;

  @property({
    comment: 'dividend option for a whole life policy',
    compositeValidators: ['policyHasValidDividendOption'],
  })
  dividendOption: string;

  @property({
    values: {
      monthly: 'Monthly',
      annual: 'Annual',
    },
    compositeValidators: ['policyHasValidDividendOption'],
  })
  paymentFrequency: string = CommonUtils.makePaymentFrequencySafeForPolicy('monthly');

  @property({ type: Array, getType: () => UwPolicyRate })
  uwRates: Array<UwPolicyRate> = [];

  @property({
    rule: ['currencyRoundedConditionally'],
    compositeValidators: ['policyHasValidFaceAmount'],
  })
  faceAmount: number | null;

  @property({
    getType: () => FollowupSummary,
    comment: 'Summary of communication between underwriter and the customer',
  })
  followupSummary: FollowupSummary;

  @property({ getType: () => PolicyFollower })
  policyFollowers: Array<PolicyFollower> = [];

  // we do not have access to follower id on frontend,
  // so we have to check for following on the backend
  // this local property helps in carrying the result to the frontend
  @property({ toServer: false, persist: false })
  isLoggedInPrincipalFollowing: boolean;

  @property({ type: Array, getType: () => PolicyAgent })
  servicingPolicyAgents: Array<PolicyAgent> = [];

  @property({ comment: 'maxTerm for CASTermECP amendments' })
  maxTermECP: number | null = null;

  @property({ comment: 'is product switching allowed from underwriting' })
  productSwitchingAllowed: boolean = false;

  @property({
    comment:
      'If this policy includes an ADBR (Accelerated Death Benefit Rider). True for all policies except some Sanderling policies',
  })
  includeAdbr: boolean = true;

  @property({
    isLocal: true,
    rule: ['currencyRoundedConditionally'],
  })
  set girRiderAmount(value: number) {
    const girRider: PolicyRider = this.getRider(RiderType.GIR);
    if (girRider) {
      // the amount passed in here has already been validated by UI validation rules.
      girRider.amount = value;
    }
  }

  get girRiderAmount(): number {
    const girRider: PolicyRider = this.getRider(RiderType.GIR);
    return girRider ? girRider.amount : null;
  }

  @property({
    isLocal: true,
    rule: ['currencyRoundedConditionally'],
  })
  set rtrRiderAmount(value: number) {
    const rtrRider: PolicyRider = this.getRider(RiderType.RTR);
    if (rtrRider) {
      // the amount passed in here has already been validated by UI validation rules.
      rtrRider.amount = value;
    }
  }

  get rtrRiderAmount(): number {
    const rtrRider: PolicyRider = this.getRider(RiderType.RTR);
    return rtrRider ? rtrRider.amount : null;
  }

  @property({
    isLocal: true,
  })
  hasLisrRider: boolean = false;

  @property({
    isLocal: true,
  })
  get cirRider(): PolicyRider {
    return (this.riders || []).find((rider) => {
      return rider.type === RiderType.CIR;
    });
  }

  set cirRider(rider: PolicyRider) {
    if (rider) {
      this.riders.push(rider);
    } else {
      this.removeRider(RiderType.CIR);
    }
  }

  @property({
    isLocal: true,
  })
  set hasCirRider(value: boolean) {
    if (value) {
      this.addRider(RiderType.CIR);
    } else {
      this.removeRider(RiderType.CIR);
    }
  }

  get hasCirRider(): boolean {
    const cirRider: PolicyRider = this.getRider(RiderType.CIR);
    return !!cirRider;
  }

  @property({
    isLocal: true,
    rule: ['currencyZero', 'currencyRoundedConditionally', 'required'],
    resetOnError: true,
  })
  set cirRiderAmount(value: number) {
    const cirRider: PolicyRider = this.getRider(RiderType.CIR);
    if (cirRider) {
      // the amount passed in here has already been validated by UI validation rules.
      cirRider.amount = value;
    }
  }

  get cirRiderAmount(): number {
    const cirRider: PolicyRider = this.getRider(RiderType.CIR);
    return cirRider ? cirRider.amount : null;
  }

  @property({
    isLocal: true,
  })
  set benefitDisbursementMethod(value: number) {
    const cirRider: PolicyRider = this.getRider(RiderType.CIR);
    if (cirRider) {
      // the amount passed in here has already been validated by UI validation rules.
      cirRider.benefitDisbursementMethod = value;
    }
  }

  get benefitDisbursementMethod(): number {
    const cirRider: PolicyRider = this.getRider(RiderType.CIR);
    return cirRider ? cirRider.benefitDisbursementMethod : null;
  }

  @property({
    isLocal: true,
  })
  set puaAddedToBenefitPool(value: boolean) {
    const cirRider: PolicyRider = this.getRider(RiderType.CIR);
    if (cirRider) {
      // the amount passed in here has already been validated by UI validation rules.
      cirRider.puaAddedToBenefitPool = value;
    }
  }

  get puaAddedToBenefitPool(): boolean {
    const cirRider: PolicyRider = this.getRider(RiderType.CIR);
    return cirRider ? cirRider.puaAddedToBenefitPool : null;
  }

  @property({
    isLocal: true,
  })
  set monthlyIncreaseOption(value: string) {
    const cirRider: PolicyRider = this.getRider(RiderType.CIR);
    if (cirRider) {
      // the amount passed in here has already been validated by UI validation rules.
      cirRider.monthlyIncreaseOption = value === 'true';
    }
  }

  get monthlyIncreaseOption(): string | null {
    const cirRider: PolicyRider = this.getRider(RiderType.CIR);
    if (cirRider) {
      return cirRider.monthlyIncreaseOption ? 'true' : 'false';
    }
    return null;
  }

  constructor() {
    super();
  }

  static new(customer, type?) {
    return new CoverpathPolicy().init(customer, type);
  }

  resetWorkflowProps() {
    super.resetWorkflowProps();
    this.uwRates = [];
    this.policyUpdatedTime = null;
    this.isAutoPay = true;
  }

  get allServicingAgents(): Array<Agent> {
    const agents: Array<Agent> = [];
    const primaryAgent = this.primaryPolicyAgent ? this.primaryPolicyAgent.primaryAgent : null;
    if (primaryAgent) {
      agents.push(primaryAgent);
    }
    this.additionalPolicyAgents.forEach((additionalAgent) => {
      agents.push(additionalAgent.agent);
    });

    // For a Sanderling policy, an active primary advisor and an active servicing advisor can co-exist.
    // In above scenario, an active servicing advisor is not primary advisor or secondary (additional)
    // advisor, so the active servicing advisor should also be included using the logic below.
    if (CoverpathUtils.isSanderlingPolicy(this.product)) {
      const currDate = new Date();
      this.servicingPolicyAgents.forEach((servicingAgent) => {
        // If a servicing advisor has null endDate or future endDate, then include this servicing advisor.
        if (!servicingAgent.endDate || servicingAgent.endDate > currDate) {
          agents.push(servicingAgent.agent);
        }
      });
    }

    return agents;
  }

  gatherRates(): Promise<void> {
    const request: PremiumRequest = {
      product: this.product,
      productType: Product.products[this.product].type.code,
      state: this.insured.address.state,
      gender: this.insured.person.gender,
      ratingCode: null,
      term: this.term,
      dob: this.insured.person.dob,
      effectiveDate: this.effectiveDate || new Date(),
      face: this.faceAmount,
      waiver: this.waiver,
      paymentFrequency: this.paymentFrequency,
      flatExtras: Policy.getUWFlatExtras(this.premiumBreakdown.flatExtras),
      riders: this.riders
        .map((rider) => ({
          type: rider.type,
          amount: rider.amount,
          accelerationPercent: rider.benefitDisbursementMethod,
          monthlyIncreaseOption: rider.monthlyIncreaseOption,
        }))
        .filter((rider) => {
          if (rider.type === RiderType.ALIR) {
            return false;
          }
          if (
            rider.type === RiderType.CIR &&
            (!Utils.exists(rider.amount) || !Utils.exists(rider.accelerationPercent))
          ) {
            return false;
          }
          return Utils.exists(rider.amount);
        }),
      asOfDate: new Date(),
    };

    const rates: Array<UwPolicyRate> = [];
    return this.fetchPolicyRates(request)
      .then((results) => {
        results.forEach((result) => {
          rates.push(UwPolicyRate.new(this, request, result.response, result.rate));
        });
      })
      .then(() => {
        this.uwRates = rates;
      });
  }

  fetchPolicyRates(request: PremiumRequest): Promise<UwPolicyRateResult[]> {
    const promises = [];
    [
      RateClasses.ULTRA,
      RateClasses.SELECT,
      RateClasses.STANDARD,
      RateClasses.SELECT_TOBACCO,
      RateClasses.STANDARD_TOBACCO,
    ].forEach((rate) => {
      promises.push(
        new Promise((resolve, reject) => {
          request.ratingCode = rate.code;
          ProductServiceClient.getPremium(this.policyNumber, null, request)
            .then((response) => {
              resolve({ rate: rate.abbr, response: response });
            })
            .catch((e) => {
              UILoggerSingleton.getInstance().info(
                {
                  module: 'Policy',
                  function: 'gatherRates',
                  category: UICategory.request,
                },
                'failed-to-fetch-rates-for-policy id:' + this._id
              );
              reject();
            });
        })
      );
    });
    return Promise.all(promises);
  }

  private getQuoteOrFinalPremium(isAnnual: boolean) {
    let premBd = this.premiumBreakdown;
    if (premBd.base === 0) {
      premBd = this.appliedForPremiumBreakdown;
    }
    let totalPremium = premBd.getTotal();
    // this arithmetic is EVIL, monthly * 12 does NOT equal the annual premium
    // this is an acceptable circumstance for PA docusign ONLY!
    if (isAnnual && this.paymentFrequency.toLowerCase() === 'monthly') {
      totalPremium *= 12;
    } else if (!isAnnual && this.paymentFrequency.toLowerCase() === 'annual') {
      totalPremium *= PremiumBreakdown.YEARLY_TO_MONTHLY_RATIO;
    }
    return Utils.formatCurrency(totalPremium, 2);
  }

  getCoverpathPAPolicyPremium(): string {
    return this.getQuoteOrFinalPremium(false);
  }

  getCoverpathPAWLTotalPremium(): string {
    return this.getQuoteOrFinalPremium(true);
  }

  stripPolicyInfo() {
    // strips policy to barebones regardless of WL/Term
    UILoggerSingleton.getInstance().info(
      {
        module: 'CoverpathPolicy',
        function: 'stripPolicyInfo',
        category: UICategory.request,
        data: {
          appNumber: this.application.applicationNumber,
        },
      },
      'Stripping policy info'
    );

    // remove all riders where applicable
    this.riders.forEach((rider, idx) => {
      rider.remove();
      this.riders.splice(idx, 1);
    });

    // reset core policy details
    this.product = null;
    this.productType = null;
    this.term = null;

    // reset additional policy details
    this.loanInterestType = undefined;
    this.dividendOption = undefined;
    this.wholeAPL = undefined;

    this.hasEcp = false;
  }

  setDefaultInAppTermPolicy() {
    this.stripPolicyInfo();

    UILoggerSingleton.getInstance().info(
      {
        module: 'CoverpathPolicy',
        function: 'setDefaultInAppTermPolicy',
        category: UICategory.request,
        data: {
          appNumber: this.application.applicationNumber,
        },
      },
      'Setting default term policy for app'
    );

    this.product = Product.products.CASTerm.code;
    this.productType = ProductType.TERM;
    this.faceAmount =
      this.faceAmount < Product.productSpecs.CASTerm.minFace ? Product.productSpecs.CASTerm.minFace : this.faceAmount;
    this.term = Product.productSpecs.CASTerm.minTerm;
  }

  setDefaultInAppWholeLifePolicy() {
    // 1035 removal needs special attn, only applicable for WL
    if (this.hasAlirRider && this.alirRider.exchange1035) {
      this.alirRider.remove1035();
    }

    this.stripPolicyInfo();

    // setup: run rider trigger functions after removing riders in stripInAppPolicyInfo
    this.hasCirRiderTrigger();
    this.hasAlirRiderTrigger();

    UILoggerSingleton.getInstance().info(
      {
        module: 'CoverpathPolicy',
        function: 'setDefaultInAppWholeLifePolicy',
        category: UICategory.request,
        data: {
          appNumber: this.application.applicationNumber,
        },
      },
      'Setting default whole life policy for app'
    );
    this.product = Product.products.CPL100A.code;
    this.productType = ProductType.WHOLE;
    this.term = Product.productSpecs.CPL100A.term;

    this.loanInterestType = LoanType.VARIABLE;
    this.dividendOption = 'PUA';
    this.wholeAPL = undefined;
  }

  getCoverpathPAWLBasePremium(): string {
    let premBd = this.premiumBreakdown;
    if (premBd.base === 0) {
      premBd = this.appliedForPremiumBreakdown;
    }
    let totalBase = premBd.base + premBd.getTotalFlatExtras() + this.getTotalFlatExtrasForRiders();
    // this arithmatic is EVIL, montly * 12 does NOT equal the annual premium
    // this is an acceptable circumstance for PA docusign ONLY!
    if (this.paymentFrequency.toLowerCase() === 'monthly') {
      totalBase *= 12;
    }
    return Utils.formatCurrency(totalBase);
  }

  paymentFrequencyTrigger() {
    super.paymentFrequencyTrigger();
    if (this.isPartOfJointApp() && this.isPrimary) {
      this.application.secondaryPolicy.isAutoPay = true;
    }
    this.isAutoPay = true;
  }

  isAutoPayTrigger() {
    if (this.isPartOfJointApp() && this.isPrimary) {
      this.application.secondaryPolicy.isAutoPay = this.isAutoPay;
    }
  }

  paymentInApplicationTrigger() {
    if (this.isPartOfJointApp() && this.isPrimary) {
      this.application.secondaryPolicy.isAutoPay = true;
    }
    this.isAutoPay = true;
    this.giactWebDebitResponseCode = null;
    this.isGiactWebDebitValid = null;
    this.lastWebDebitAttemptCombination = null;
  }

  dividendOptionTrigger() {
    if (
      this.hasCirRider &&
      this.dividendOption !== DividendOption.PUA &&
      this.dividendOption !== DividendOption.PRPUA
    ) {
      this.cirRider.puaAddedToBenefitPool = false;
    }
  }

  private getCoverpathPARiderPremiumByIndex(index: number): string {
    let premium = this.getRiderByIndex(index).premiumBase;
    if (this.paymentFrequency.toLowerCase() === 'monthly') {
      premium *= 12;
    }
    return Utils.formatCurrency(premium, 2);
  }

  updateWithQuoteInfo(quote: BaseQuote): void {
    super.updateWithQuoteInfo(quote);

    if (quote.productType === Product.productTypes.Whole.code) {
      this.dividendOption = (quote as CoverpathWholeLifeQuote).dividend;
      this.loanInterestType = (quote as CoverpathWholeLifeQuote).loanType;
    }
    this.updatePartyDuringInitialAppLoad(quote);

    this.hasEcp = quote.product === Product.products.CASTermECP.code ? true : false;
  }

  // moved this override and wrapped it so it doesn't reset values if they already exist
  updatePartyDuringInitialAppLoad(quote: BaseQuote): void {
    if (!this.insured.person.gender) {
      this.insured.person.gender = quote.gender;
      this.insured.person.genderTrigger();
    }
    if (!this.insured.person.dob) {
      this.insured.person.dob = quote.dateOfBirth;
      this.insured.person.dobTrigger();
    }

    if (!this.insured.person.residencyStatus) {
      this.insured.person.residencyStatus = quote.citizenship;
    }
  }

  // overriding the function tied to metadata that checks if the waiver of premium question should be shown
  getWaiverPremium(): number | null {
    // Waiver of premium question shown only if actual age <= 58
    const quote = this.customer.currentQuote;
    const actualAge = this.insured.getAge();
    const quoteAge = Utils.yearsBetween(quote.dateOfBirth, Utils.now());
    if ((!actualAge || actualAge > 58) && quoteAge > 58) {
      return null;
    }

    return quote.waiverOfPremiumAmount && quote.waiverOfPremiumAmount > 0 ? quote.waiverOfPremiumAmount : null;
  }

  public getMaxFaceInCurrentStageBy50KIncrements(): number | null {
    // this is used for amendments (currently only Haven)
    // CP will eventually also need this once we have amendments
    return null;
  }

  getRiderMonthly() {
    let wop: number;
    if (!this.isAccelerated() && !this.isTLIC()) {
      // KO policy. Use the sync quoteEngine here until Docusign's functions are changed to handle
      // async pattern
      const rating = this.isSmokerByApp() ? RateClasses.STANDARD_TOBACCO.code : RateClasses.STANDARD.code;
      const premium = this.getQuoteSync(rating);
      wop = premium.waiver;
    } else {
      wop = this.premiumBreakdown.waiver;
    }

    return 'The cost is included in the premium for the policy.\n' + (this.waiver ? Utils.formatCurrency(wop, 2) : '');
  }

  getNumDaysToConfirmPolicy(): number {
    return 30;
  }

  /**
   * used by conversion flow
   */
  updateProperties(overrides: Partial<CoverpathPolicy>): void {
    Object.keys(overrides).forEach((prop) => {
      this[prop] = overrides[prop];
    });
  }

  getRiderByIndex(index: number) {
    return this.riders[index];
  }

  /**
   * @deprecated
   * Please get this data from PA instead
   */
  getLoanInterestRate(): string {
    return `5.0`;
  }

  getAllAmendmentDetails(): string {
    return CoverpathUtils.getAmendmentDetailsTextForApplication(this.application).join(' ');
  }

  /*
    Below functions are not be called for CoverpathPolicy,
    but to make sure they are not going to be, override them here with empty implementation.
    Should remove them after Haven/CP repo separation.
   */
  waiverTrigger() {
    UILoggerSingleton.getInstance().warn(
      {
        module: 'CoverpathPolicy',
        function: 'waiverTrigger',
        category: UICategory.request,
      },
      'This function should not be called.'
    );
  }

  getInitialPremium(): string {
    UILoggerSingleton.getInstance().warn(
      {
        module: 'CoverpathPolicy',
        function: 'getInitialPremium',
        category: UICategory.request,
      },
      'This function should not be called.'
    );
    return null;
  }

  getPolicyFaceValue() {
    UILoggerSingleton.getInstance().warn(
      {
        module: 'CoverpathPolicy',
        function: 'getPolicyFaceValue',
        category: UICategory.request,
      },
      'This function should not be called.'
    );
    return null;
  }

  getPolicyPremium() {
    UILoggerSingleton.getInstance().warn(
      {
        module: 'CoverpathPolicy',
        function: 'getPolicyPremium',
        category: UICategory.request,
      },
      'This function should not be called.'
    );
    return null;
  }

  getPolicyFace() {
    return `$${this.faceAmount.toLocaleString('en')}`;
  }

  getPolicyTermValue() {
    UILoggerSingleton.getInstance().warn(
      {
        module: 'CoverpathPolicy',
        function: 'getPolicyTermValue',
        category: UICategory.request,
      },
      'This function should not be called.'
    );
    return null;
  }

  getSecondaryPolicyNumberIfJointApp() {
    if (this.application && this.application.secondaryPolicy && this.isPartOfJointApp()) {
      return this.application.secondaryPolicy.policyNumber;
    }

    return '';
  }

  public addPolicyFollower(follower: Agent | AgentProxy | OpsProxy, entitlement: Entitlement): void {
    if (!this.policyFollowers.find((policyFollower) => policyFollower.followerId === follower._id)) {
      const policyFollower = PolicyFollower.new(this, follower, entitlement);
      this.policyFollowers.push(policyFollower);
    }
  }

  public async removePolicyFollower(follower: Agent | AgentProxy | OpsProxy): Promise<void> {
    const policyFollower = this.policyFollowers.find((policyFollower) => policyFollower.followerId === follower._id);
    this.policyFollowers = this.policyFollowers.filter((policyFollower) => policyFollower.followerId !== follower._id);
    if (policyFollower) {
      await policyFollower.persistorDelete();
    }
  }

  requiresNWMReplacementFormsOwnerIsInsured(): boolean {
    const existingPolicies = Utils.get(this, 'application.primaryPolicy.insured.existingPolicies');
    return this.isOwnerSameAsInsuredOrJuvenile()
      ? !!(
          this.isCoverpathPolicy() &&
          this.arePolicyAgentsOnWatchForNWMReplacement() &&
          existingPolicies &&
          existingPolicies.hasNWMReplacement()
        )
      : false;
  }

  requiresNWMReplacementFormsOwnerIsNotInsured(): boolean {
    const existingPolicies = Utils.get(this, 'application.primaryPolicy.insured.existingPolicies');
    return this.isOwnerOtherThanInsuredAndNotJuvenile()
      ? !!(
          this.isCoverpathPolicy() &&
          this.arePolicyAgentsOnWatchForNWMReplacement() &&
          existingPolicies &&
          existingPolicies.hasNWMReplacement()
        )
      : false;
  }

  requiresNWMReplacementFormsOwnerIsInsuredSingleOwner(): boolean {
    return this.requiresNWMReplacementFormsOwnerIsInsured() && !this.hasSecondOwner();
  }

  requiresNWMReplacementFormsOwnerIsInsuredHasSecondOwner(): boolean {
    return this.requiresNWMReplacementFormsOwnerIsInsured() && this.hasSecondOwner();
  }

  requiresNWMReplacementFormsOwnerIsNotInsuredSingleOwner(): boolean {
    return this.requiresNWMReplacementFormsOwnerIsNotInsured() && !this.hasSecondOwner();
  }

  requiresNWMReplacementFormsOwnerIsNotInsuredHasSecondOwner(): boolean {
    return this.requiresNWMReplacementFormsOwnerIsNotInsured() && this.hasSecondOwner();
  }

  arePolicyAgentsOnWatchForNWMReplacement(): boolean {
    const agent = Utils.get(this, 'primaryPolicyAgent.agent');
    return (
      (agent && agent.onWatchForNWMReplacement()) ||
      this.additionalPolicyAgents.some((policyAgent: PolicyAgent) => policyAgent.agent.onWatchForNWMReplacement())
    );
  }

  getNWMAgentsOnWatch(): string {
    const agents = [];
    if (this.primaryPolicyAgent.agent.onWatchForNWMReplacement()) {
      agents.push(this.primaryPolicyAgent.agent.displayNameWithLastName);
    }

    this.additionalPolicyAgents
      .filter((policyAgent: PolicyAgent) => policyAgent.agent.onWatchForNWMReplacement())
      .forEach((policyAgent) => agents.push(policyAgent.agent.displayNameWithLastName));

    return agents.join(', ').replace(/, ([^,]*)$/, ' & $1');
  }

  private policyHasPendingAmendment(policy: Policy): boolean {
    return !policy
      .getPendingAmendments()
      .every(
        (pendingAmendment) =>
          pendingAmendment.property === PolicyAmendmentProperty.BASE ||
          pendingAmendment.property === PolicyAmendmentProperty.GIR_PREMIUM_BASE ||
          pendingAmendment.property === PolicyAmendmentProperty.GIR_PREMIUM_WAIVER ||
          pendingAmendment.property === PolicyAmendmentProperty.RTR_PREMIUM_BASE ||
          pendingAmendment.property === PolicyAmendmentProperty.RTR_PREMIUM_WAIVER ||
          pendingAmendment.property === PolicyAmendmentProperty.WAIVER_AMOUNT ||
          pendingAmendment.property === PolicyAmendmentProperty.EFFECTIVE_DATE ||
          pendingAmendment.property === PolicyAmendmentProperty.PRODUCT_PREMIUM_VERSION
      );
  }

  hasPendingAmendment(): boolean {
    const primaryPolicy = this.application.primaryPolicy;
    const secondaryPolicy = this.application.secondaryPolicy;
    const secondaryPolicyWithPendingAmendment = this.application.secondaryPolicyWithPendingAmendment;

    let primaryPolicyHasPendingAmendments = false,
      secondaryPolicyHasPendingAmendments = false,
      secondaryPolicyWithPendingAmendmentHasPendingAmendments = false;

    if (primaryPolicy.getPendingAmendments().length > 0) {
      // For coverpath policies we want to ignore the save age amendment. Saving age could change premiums
      primaryPolicyHasPendingAmendments = this.policyHasPendingAmendment(primaryPolicy);
    }

    if (secondaryPolicy && secondaryPolicy.getPendingAmendments().length > 0) {
      secondaryPolicyHasPendingAmendments = this.policyHasPendingAmendment(secondaryPolicy);
    }

    if (secondaryPolicyWithPendingAmendment && secondaryPolicyWithPendingAmendment.getPendingAmendments().length > 0) {
      secondaryPolicyWithPendingAmendmentHasPendingAmendments = this.policyHasPendingAmendment(
        secondaryPolicyWithPendingAmendment
      );
    }

    return (
      primaryPolicyHasPendingAmendments ||
      secondaryPolicyHasPendingAmendments ||
      secondaryPolicyWithPendingAmendmentHasPendingAmendments
    );
  }

  hasHadPolicyChanges() {
    if (this.policyChanges) {
      const completedChanges = this.policyChanges.filter(function (policyChange) {
        return policyChange.status === POLICY_CHANGE_HISTORY_STATUS.COMPLETE;
      });
      // For coverpath policies we want to ignore the save age amendment. Saving age could change premiums
      return (
        completedChanges.length > 0 &&
        !completedChanges.every(
          (change) =>
            change.property === PolicyAmendmentProperty.BASE ||
            change.property === PolicyAmendmentProperty.GIR_PREMIUM_BASE ||
            change.property === PolicyAmendmentProperty.GIR_PREMIUM_WAIVER ||
            change.property === PolicyAmendmentProperty.RTR_PREMIUM_BASE ||
            change.property === PolicyAmendmentProperty.GIR_PREMIUM_WAIVER ||
            change.property === PolicyAmendmentProperty.WAIVER_AMOUNT ||
            change.property === PolicyAmendmentProperty.EFFECTIVE_DATE ||
            change.property === PolicyAmendmentProperty.PRODUCT_PREMIUM_VERSION
        )
      );
    }

    return false;
  }

  // Overriding Policy
  getQuoteHealthClass(): string {
    return this.application.quote.healthClass;
  }

  getProductNameWithPendingAmendment() {
    const term = this.termWithPendingAmendment();
    if (term === 1) {
      return Product.products.CASTerm.artDescription;
    } else {
      return this.getProductInfoWithPendingAmendment().name + ' ' + term + ' year policy';
    }
  }

  getProductInfoWithPendingAmendment() {
    return Product.products[this.productWithPendingAmendment()];
  }

  getProductTypeInfoWithPendingAmendment() {
    return Product.productTypes[this.productTypeWithPendingAmendment()];
  }

  getPolicyProductNameString(): string {
    if (this.isPartOfJointApp()) {
      return `${(this.application.primaryPolicy as CoverpathPolicy).getProductNameWithPendingAmendment()}, ${(
        this.application.secondaryPolicy as CoverpathPolicy
      ).getProductNameWithPendingAmendment()}`;
    }
    return this.getProductNameWithPendingAmendment();
  }
  productWithPendingAmendment(): string {
    return this.withPendingAmendment(PolicyAmendmentProperty.PRODUCT) as string;
  }

  productTypeWithPendingAmendment(): string {
    return this.withPendingAmendment(PolicyAmendmentProperty.PRODUCT_TYPE) as string;
  }

  hasGirRiderWithPendingAmendment(): boolean {
    return this.withPendingAmendment(PolicyAmendmentProperty.HAS_GIR_RIDER) === 'true';
  }

  hasRtrRiderWithPendingAmendment(): boolean {
    return this.withPendingAmendment(PolicyAmendmentProperty.HAS_RTR_RIDER) === 'true';
  }

  hasCirRiderWithPendingAmendment(): boolean {
    return this.withPendingAmendment(PolicyAmendmentProperty.HAS_CIR_RIDER) === true;
  }

  effectiveDateWithPendingAmendment(): Date | null {
    return this.withPendingAmendment(PolicyAmendmentProperty.EFFECTIVE_DATE) as Date | null;
  }

  productPremiumVersionWithPendingAmendment(): string {
    return this.withPendingAmendment(PolicyAmendmentProperty.PRODUCT_PREMIUM_VERSION) as string;
  }

  getTotalFlatExtrasForRiders(): number {
    return this.riders.reduce((total, rider) => total + rider.getTotalFlatExtras(), 0);
  }

  getTotalFlatExtrasForRidersWithPendingAmendment(): number {
    return this.ridersWithPendingAmendment().reduce(
      (total, rider) => total + PremiumBreakdown.getTotalFlatExtras(rider.flatExtras),
      0
    );
  }

  //Includes WOP
  getTotalWaiverPremiumForRiders(premBd?: PremiumBreakdown): number {
    if (!premBd) {
      premBd = this.premiumBreakdown;
    }
    return this.riders.reduce(
      (total, rider) => total + (this.waiver ? rider.premiumWaiver : 0),
      this.waiver ? premBd.waiver : 0
    );
  }

  //Includes WOP
  getTotalWaiverPremiumForRidersWithPendingAmendment(): number {
    return this.ridersWithPendingAmendment().reduce(
      (total, rider) => total + rider.premiumWaiver,
      this.hasWaiverWithPendingAmendment() ? this.premiumBreakdown.waiverAmountWithPendingAmendment() : 0
    );
  }

  getTotalBasePremiumForRiders(): number {
    return this.riders.reduce((total, rider) => total + rider.premiumBase, 0);
  }

  getTotalBasePremiumForRidersWithPendingAmendment(): number {
    return this.ridersWithPendingAmendment().reduce((total, rider) => total + rider.premiumBase, 0);
  }

  hasPendingEffectiveDateAmendment(): boolean {
    const primaryPolicy = this.application.primaryPolicy;
    const secondaryPolicy = this.application.secondaryPolicy;

    const primaryPolicyHasPendingEffectiveDateAmendment = primaryPolicy
      .getPendingAmendments()
      .some((pendingAmendment) => pendingAmendment.property === PolicyAmendmentProperty.EFFECTIVE_DATE);

    let secondaryPolicyHasPendingEffectiveDateAmendment = false;
    if (secondaryPolicy) {
      secondaryPolicyHasPendingEffectiveDateAmendment = secondaryPolicy
        .getPendingAmendments()
        .some((pendingAmendment) => pendingAmendment.property === PolicyAmendmentProperty.EFFECTIVE_DATE);
    }

    return primaryPolicyHasPendingEffectiveDateAmendment || secondaryPolicyHasPendingEffectiveDateAmendment;
  }

  hasAlirRiderWithPendingAmendment(): boolean {
    return this.withPendingAmendment(PolicyAmendmentProperty.HAS_ALIR_RIDER) === true;
  }

  ridersWithPendingAmendment(): Array<PolicyRider> {
    const riders: Array<PolicyRider> = [];
    if (this.hasGirRiderWithPendingAmendment()) {
      const rider = PolicyRider.new(RiderType.GIR, null);
      rider.amount = this.girRider.amountWithPendingAmendment();
      rider.premiumBase = this.girRider.premiumBaseWithPendingAmendment();
      rider.premiumWaiver = this.hasWaiverWithPendingAmendment()
        ? this.girRider.premiumWaiverWithPendingAmendment()
        : 0;
      rider.flatExtras = FlatExtraAmendments.fetchRiderFlatExtrasWithPendingAmendment(
        this,
        this.girRider.flatExtras,
        PolicyAmendmentProperty.GIR_FLAT_EXTRAS
      );
      riders.push(rider);
    }

    if (this.hasRtrRiderWithPendingAmendment()) {
      const rider = PolicyRider.new(RiderType.RTR, null);
      rider.amount = this.rtrRider.amountWithPendingAmendment();
      rider.premiumBase = this.rtrRider.premiumBaseWithPendingAmendment();
      rider.premiumWaiver = this.hasWaiverWithPendingAmendment()
        ? this.rtrRider.premiumWaiverWithPendingAmendment()
        : 0;
      rider.flatExtras = FlatExtraAmendments.fetchRiderFlatExtrasWithPendingAmendment(
        this,
        this.rtrRider.flatExtras,
        PolicyAmendmentProperty.RTR_FLAT_EXTRAS
      );
      riders.push(rider);
    }

    if (this.hasAlirRiderWithPendingAmendment()) {
      const rider = PolicyRider.new(RiderType.ALIR, null);
      rider.amount = this.alirRider.amountWithPendingAmendment();
      rider.premiumBase = this.alirRider.premiumBaseWithPendingAmendment() || 0;
      rider.premiumWaiver = 0;
      rider.plannedYears = this.alirRider.plannedYearsWithPendingAmendment();
      rider.paymentFrequency = this.alirRider.paymentFrequencyWithPendingAmendment();
      rider.hasScheduledPayments = this.alirRider.hasAlirScheduledPaymentsWithPendingAmendment();
      rider.lumpSumUses1035 = this.alirRider.hasAlir1035ExchangeWithPendingAmendment();

      if (this.alirRider.hasAlir1035ExchangeWithPendingAmendment()) {
        rider.lumpSumUses1035 = true;
        rider.exchange1035 = Exchange1035.new(
          this.alirRider.alir1035ExchangeAmountWithPendingAmendment(),
          this.alirRider.alir1035CostBasisWithPendingAmendment(),
          this.alirRider.alir1035PolicyTypeWithPendingAmendment(),
          this.alirRider.alir1035ApplyTowardsWithPendingAmendment(),
          this.alirRider.alir1035ExpectedArrivalWithPendingAmendment(),
          this.alirRider.alir1035IsMecWithPendingAmendment()
        );
      }
      riders.push(rider);
    }

    if (this.hasCirRiderWithPendingAmendment()) {
      const rider = PolicyRider.new(RiderType.CIR, null);
      rider.amount = this.cirRider.amountWithPendingAmendment();
      rider.premiumBase = this.cirRider.premiumBaseWithPendingAmendment();
      rider.premiumWaiver = this.hasWaiverWithPendingAmendment()
        ? this.cirRider.premiumWaiverWithPendingAmendment()
        : 0;
      rider.benefitDisbursementMethod = this.cirRider.benefitDisbursementMethodWithPendingAmendment();
      rider.puaAddedToBenefitPool = this.cirRider.puaAddedToBenefitPoolWithPendingAmendment();
      rider.monthlyIncreaseOption = this.cirRider.monthlyIncreaseOptionWithPendingAmendment();
      rider.predictedBenefitAge = this.cirRider.predictedBenefitAgeWithPendingAmendment();
      riders.push(rider);
    }

    return riders;
  }

  toGmtFromAdvisorTimezone(date): Date {
    return new Date(date.getTime() - Utils.milliseconds(this.getAdvisorTimezoneOffsetMins(), 'mins'));
  }

  get servicingAgent() {
    return this.primaryPolicyAgent ? this.primaryPolicyAgent.primaryAgent : null;
  }

  isCoverpathPolicy(): this is CoverpathPolicy {
    return this.constructor.name === 'CoverpathPolicy';
  }

  async clonePrimaryAgentInfo(newPolicy) {
    const cdmServiceInstance = new CdmService();
    if (newPolicy.primaryPolicyAgent) {
      //If primary policy agent is already set, get latest producer info from CDM and update properties
      const primaryAgencyCorp: AgencyCorpsRelationship = await cdmServiceInstance.getProducerAgencyCorps(
        newPolicy.primaryPolicyAgent.agent.producerId
      );
      newPolicy.primaryPolicyAgent.agencyId = primaryAgencyCorp.agencyBpid;
      newPolicy.primaryPolicyAgent.corpId = primaryAgencyCorp.corpsBpid;
      newPolicy.primaryPolicyAgent.corpAgency = JSON.stringify(primaryAgencyCorp);

      //link policy agent back to policy
      newPolicy.primaryPolicyAgent.policy = newPolicy;

      //make sure type is reset to null if this was originally a servicing agent
      newPolicy.primaryPolicyAgent.type = null;

      //default initial primary policy agent's ratios to 100
      newPolicy.primaryPolicyAgent.ratio = 100;
      newPolicy.primaryPolicyAgent.renewalCommissionRatio = 100;
    } else {
      return undefined;
    }

    //servicing agent should now be the primary agent, clear servicing agents from policy
    newPolicy.servicingPolicyAgents = [];
    newPolicy.additionalPolicyAgents = [];
  }

  async cloneAdditionalPolicyAgentInfo(newPolicy) {
    const cdmServiceInstance = new CdmService();

    //for each additional agent, get latest producer agency/corp from cdm and create policy agent
    for (const additionalPolicyAgent of this.additionalPolicyAgents) {
      if (additionalPolicyAgent.agent.producerId === newPolicy.primaryPolicyAgent.agent.producerId) {
        //this case will be true if previous servicing agent was also an additional agent.
        //in that case, they will now be the primary policy agent. No need to add them as additional.
        continue;
      }
      try {
        const additonalAgencyCorp: AgencyCorpsRelationship = await cdmServiceInstance.getProducerAgencyCorps(
          additionalPolicyAgent.agent.producerId
        );

        const newAdditionalPolicyAgent: PolicyAgent = PolicyAgent.new(
          newPolicy,
          additionalPolicyAgent.agent,
          additionalPolicyAgent.ratio || 0,
          additionalPolicyAgent.renewalCommissionRatio || 0,
          additonalAgencyCorp.agencyBpid,
          additonalAgencyCorp.corpsBpid,
          JSON.stringify(additonalAgencyCorp)
        );
        newPolicy.additionalPolicyAgents.push(newAdditionalPolicyAgent);

        //as we add additional policy agents, we will deduct the ratio from the primary policy agent's ratio which is defaulted at 100
        newPolicy.primaryPolicyAgent.ratio -= newAdditionalPolicyAgent.ratio;
        newPolicy.primaryPolicyAgent.renewalCommissionRatio -= newAdditionalPolicyAgent.renewalCommissionRatio;
      } catch (e) {
        continue;
      }
    }
  }
  async clonePolicyAgentInfo(newPolicy) {
    await this.clonePrimaryAgentInfo(newPolicy);
    await this.cloneAdditionalPolicyAgentInfo(newPolicy);
  }

  async createClonedPolicy(keepOriginal?: boolean, sourceType?: ApplicationSourceType): Promise<Policy> {
    const newPolicy = (await super.createClonedPolicy(keepOriginal, sourceType)) as CoverpathPolicy;

    //if policy is being duplicated and is in force, use new cloning logic for policy agents
    if (sourceType === ApplicationSourceType.DUPLICATE && this.isInForce()) {
      await this.clonePolicyAgentInfo(newPolicy);
    } else {
      //all other inflight policies cloning logic
      // If this policy had a policy agent, make sure to link it back.
      if (newPolicy.primaryPolicyAgent) {
        newPolicy.primaryPolicyAgent.policy = newPolicy;
        //set primary producer commissions to default 100 if there are null values
        const fyc = newPolicy.primaryPolicyAgent.ratio;
        const ryc = newPolicy.primaryPolicyAgent.renewalCommissionRatio;
        newPolicy.primaryPolicyAgent.ratio = typeof fyc === 'number' ? fyc : 100;
        newPolicy.primaryPolicyAgent.renewalCommissionRatio = typeof ryc === 'number' ? ryc : 100;
      }
      newPolicy.additionalPolicyAgents = [];

      for (const additionalPolicyAgent of this.additionalPolicyAgents) {
        const newAdditionalPolicyAgent: PolicyAgent = PolicyAgent.new(
          newPolicy,
          additionalPolicyAgent.agent,
          additionalPolicyAgent.ratio,
          additionalPolicyAgent.renewalCommissionRatio,
          additionalPolicyAgent.agencyId,
          additionalPolicyAgent.corpId,
          additionalPolicyAgent.corpAgency
        );
        //set additional agent commission ratios to default 0 if there are null values
        newAdditionalPolicyAgent.ratio = newAdditionalPolicyAgent.ratio || 0;
        newAdditionalPolicyAgent.renewalCommissionRatio = newAdditionalPolicyAgent.renewalCommissionRatio || 0;
        newPolicy.additionalPolicyAgents.push(newAdditionalPolicyAgent);
      }
    }

    // the superclass clone does not clone the riders by definition
    if (this.riders && this.riders.length > 0) {
      newPolicy.riders = [];
      const existingRiders: PolicyRider[] = this.riders;
      existingRiders.forEach((oldRider) => {
        const newRider: PolicyRider = oldRider.createCopy((obj, prop, template) => {
          switch (template.amorphicClassName) {
            case 'CoverpathPolicy': // don't clone the parent
            case 'FlatExtra': // don't clone flatExtras
            case 'Exchange1035': // don't clone Exchange1035s
              return undefined;
          }
          return null;
        });
        if (oldRider.exchange1035) {
          newRider.exchange1035 = Exchange1035.new(
            oldRider.exchange1035.cashValueTransfer,
            oldRider.exchange1035.costBasis,
            oldRider.exchange1035.policyType,
            oldRider.exchange1035.applyTowards,
            oldRider.exchange1035.expectedArrival,
            oldRider.exchange1035.isMec
          );
        }
        newRider.policy = newPolicy;
        newPolicy.riders.push(newRider);
      });
    }

    // effectiveDate gets cleared in resetWorkflowProps()
    // For AAA copy effectiveDate if it was set via quote
    if (sourceType === ApplicationSourceType.SEND_AGENT_ASSISTED) {
      newPolicy.effectiveDate = this.effectiveDate;
    }

    return newPolicy;
  }

  isRiderRemovedByUW(riderProperty: PolicyAmendmentProperty): boolean {
    const pendingPCH: PolicyChangeHistory = this.getPendingPolicyChange(riderProperty);
    if (pendingPCH) {
      let value = false;
      if (
        riderProperty === PolicyAmendmentProperty.HAS_GIR_RIDER ||
        riderProperty === PolicyAmendmentProperty.HAS_RTR_RIDER
      ) {
        value = (pendingPCH.getTypedNewValue() as string) === 'true';
      } else {
        value = pendingPCH.getTypedNewValue() as boolean;
      }
      return pendingPCH.editor === POLICY_CHANGE_EDITOR.AUTO && !value;
    }

    return false;
  }

  loanInterestTypeWithPendingAmendment(): LoanType {
    return this.withPendingAmendment(PolicyAmendmentProperty.LOAN_INTEREST_TYPE) as LoanType;
  }

  dividendOptionWithPendingAmendment(): string {
    return this.withPendingAmendment(PolicyAmendmentProperty.DIVIDEND_OPTION) as string;
  }

  wholeAPLWithPendingAmendment(): boolean {
    return this.withPendingAmendment(PolicyAmendmentProperty.WHOLE_APL) as boolean;
  }

  isSwitchedToWholeLife(): boolean {
    return this.isTermLifePolicy() && this.isWholeLifePolicyWithPendingAmendment();
  }

  isSwitchedToTermLife(): boolean {
    return this.isWholeLifePolicy() && this.isTermLifePolicyWithPendingAmendment();
  }

  getPolicyTitle() {
    if (this.isWholeLifePolicy()) {
      return BasicIllustrationConstants.ProductNames[this.product];
    }

    return super.getPolicyTitle();
  }

  isSwitchedToJointApp(): boolean {
    return !this.isPartOfJointApp() && this.application.isJointAppWithPendingAmendment();
  }

  isFundingPaused(): boolean {
    return /Funding Pause/.test(this.workflowState);
  }

  // if maxFace is falsy value, use faceAmount instead
  // e.g., if maxFace is null, use faceAmount instead
  getEffectiveMaxFace(): number {
    const maxFace = CoverpathUtils.isLowFaceWholeLife(this)
      ? this.maxFaceWholeLife || LowFaceWholeLife.maxFace
      : this.maxFace;
    return maxFace || this.application.getTotalFaceAmountWithRTR();
  }

  setWorkflowState(state, subState, startTime, endTime) {
    super.setWorkflowState(state, subState, startTime, endTime);
    this.policyUpdatedTime = startTime;
  }

  requiresCIRDisclosureOwnerIsInsuredForm(): boolean {
    return this.hasCirRider && this.isOwnerSameAsInsuredOrJuvenile();
  }

  requiresCIRDisclosureOwnerIsNotInsuredForm(): boolean {
    return this.hasCirRider && this.isOwnerOtherThanInsuredAndNotJuvenile();
  }

  hasCirRiderTrigger() {
    if (this.hasCirRider && !this.insured.chronicIllnessRiderSupplement) {
      this.insured.chronicIllnessRiderSupplement = ChronicIllnessRiderSupplement.new(this.customer);
    }
    if (this.hasCirRider && !this.cirRider.predictedBenefitAge) {
      this.cirRider.predictedBenefitAge = 75;
      if (this.insured.person.getAge() >= this.cirRider.predictedBenefitAge) {
        this.cirRider.predictedBenefitAge = this.insured.person.getAge() + 1;
      }
    }
    if (!this.hasCirRider) {
      delete this.insured.chronicIllnessRiderSupplement;
    }
  }

  /**
   * An application is a "tele CMI app" if insured's insurance age > 60 OR (insured's insurance age > 16 AND total face amount > $3M)
   * @returns {boolean}
   */
  isTeleCMIRequired(): boolean {
    let isTeleCMIApp: boolean = false;
    // appMetadataVersion = null for AAA/Personalized Quote after advisor sends and before client starts app
    if (!this.appMetadataVersion || this.hasTeleCMIMetadata()) {
      const insuredInsuranceAge: number = this.insured.getInsuranceAge();
      if (insuredInsuranceAge) {
        isTeleCMIApp =
          insuredInsuranceAge > TeleCMIConstants.maxAgeTeleNotRequired ||
          (insuredInsuranceAge > TeleCMIConstants.maxJuviAgeTeleNotRequired &&
            CoverpathUtils.getTotalAmountAtRisk(this) > TeleCMIConstants.maxFaceTeleNotRequired);
      }
    }
    return isTeleCMIApp;
  }

  isTeleCMINotRequired(): boolean {
    return !this.isTeleCMIRequired();
  }

  isAgentSubmittedPolicy() {
    return this.application.isAgentSubmittedApp;
  }
}
